import React from "react";

import Card from "@busbud/design-system-components/dist/Card";
import CardContent from "@busbud/design-system-components/dist/CardContent";
import CardHeader from "@busbud/design-system-components/dist/CardHeader";
import CheckboxLabel from "@busbud/design-system-components/dist/CheckboxLabel";
import Divider from "@busbud/design-system-components/dist/Divider";
import Hidden from "@busbud/design-system-components/dist/Hidden";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Label, Switch } from "@busbud/horizon";

import { useStyles } from "./selectable-card.styles";

interface Props {
  label: string;
  extra?: React.ReactNode;
  chip?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  cardProps?: React.ComponentProps<typeof Card>;
  checkbox_shape?: React.ComponentProps<typeof CheckboxLabel>["shape"];
  dataCy?: string;
  classes?: {
    root?: string;
  };
  style?: React.CSSProperties;
  children?: React.ReactNode;
  is_switch?: boolean;
}

export const SelectableCard: React.FC<Props> = ({
  label,
  checked,
  children,
  onChange,
  extra,
  chip,
  cardProps,
  disabled = false,
  checkbox_shape = "circle",
  dataCy,
  classes: external_classes,
  style,
  is_switch
}) => {
  const { cx, classes } = useStyles();
  const has_children = !!React.Children.toArray(children).filter(child =>
    React.isValidElement(child)
  ).length;

  return (
    <Card
      bgColor={
        disabled
          ? "background.secondary.transparent"
          : checked
            ? "background.informative.transparent"
            : undefined
      }
      classes={{
        root: cx(classes.card, external_classes?.root, {
          [classes.disabled]: disabled,
          [classes.checked]: checked
        })
      }}
      borderRadius="xl"
      boxShadow={!checked && !disabled ? "sm" : "unset"}
      borderColor={
        disabled
          ? "border.secondary.transparent"
          : checked
            ? "border.informative.transparent"
            : undefined
      }
      onClick={!disabled ? onChange : undefined}
      border="sm"
      data-cy={dataCy}
      data-testid={dataCy}
      style={style}
      {...cardProps}
    >
      <CardHeader
        title={
          <Stack whiteSpace="normal" alignItems="center">
            {!!is_switch ? (
              <div className="flex items-center gap-100">
                <Switch ariaLabelledBy="switch" isChecked={checked} />
                <Label as="span">{label}</Label>
              </div>
            ) : (
              <CheckboxLabel
                label={label}
                checked={checked}
                classes={{
                  root: classes.label
                }}
                variant="text"
                shape={checkbox_shape}
              />
            )}

            <Hidden smDown>{chip}</Hidden>
          </Stack>
        }
        classes={{
          root: classes.header,
          leftContent: classes.header_left_content
        }}
      >
        {extra}
      </CardHeader>
      {!!has_children && (
        <>
          <Divider className={classes.divider} />
          <CardContent className={classes.content}>{children}</CardContent>
        </>
      )}
    </Card>
  );
};
